import { Video } from "lucide-react";

import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { GalleryItemBadges } from "@/components/flowly/GalleryItemBadges/GalleryItemBadges";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { VideoLightbox } from "@/components/flowly/Lightbox/VideoLightbox/VideoLightbox";
import { HowItWorks } from "@/components/flowly/HowItWorks/HowItWorks";
import { VideoSourceUpload } from "@/components/flowly/VideoSourceUpload/VideoSourceUpload";
import { VideoPromptArea } from "@/components/flowly/VideoPromptArea/VideoPromptArea";
import { downloadMedia } from "@/lib/mediaUtils";
import { useFramesToVideo } from "./useFramesToVideo";

export const FramesToVideoPage = () => {
  const {
    fileInputRef,
    galleryRef,
    filePreview,
    endFramePreview,
    setEndFramePreview,
    handleInputChange,
    clearFile,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleEndFrameUpload,
    swapFrames,
    videoPrompt,
    setVideoPrompt,
    videoDuration,
    setVideoDuration,
    videoResolution,
    setVideoResolution,
    videoModel,
    soundEnabled,
    setSoundEnabled,
    aspectRatio,
    setAspectRatio,
    availableAspectRatios,
    availableDurations,
    availableResolutions,
    supportsEndFrame,
    supportsSound,
    alwaysIncludesAudio,
    modelDisplayName,
    promptPlaceholder,
    handleGenerateVideo,
    handleEnhancePrompt,
    isEnhancingVideoPrompt,
    isGenerating,
    getVideoCreditCost,
    costsLoading,
    galleryVideos,
    pendingCount,
    dbPendingVideos,
    videoCompletingIds,
    selectedMedia,
    setSelectedMedia,
    handleDownload,
    handleShare,
    handleRegenerate,
    handleCloseLightbox,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    availableModels,
    handleVideoModelChange,
    getVideoModelCost,
    modelWarning,
    handleDisabledClick,
    locale,
    t,
  } = useFramesToVideo();

  return (
    <StudioPageLayout
      activeTab="videos"
      activeApp="framesToVideo"
      seoHeadingKey="framesToVideo"
      headerChildren={<HowItWorks flowId="framesToVideo" />}
      footer={
        !selectedMedia ? (
          <GenerationBar
            onGenerate={handleGenerateVideo}
            generating={false}
            disabled={!filePreview || !videoPrompt.trim() || !endFramePreview}
            creditCost={getVideoCreditCost(videoDuration, videoResolution, videoModel, soundEnabled && !alwaysIncludesAudio)}
            costsLoading={costsLoading}
            buttonLabel={t.generate.createVideoBtn}
            selectedModelId={videoModel}
            onModelChange={handleVideoModelChange}
            mediaType="video"
            models={availableModels}
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
            availableAspectRatios={availableAspectRatios}
            videoDuration={videoDuration}
            onVideoDurationChange={setVideoDuration}
            availableDurations={availableDurations}
            videoResolution={videoResolution}
            onVideoResolutionChange={setVideoResolution}
            availableResolutions={availableResolutions}
            soundEnabled={supportsSound ? soundEnabled : undefined}
            onSoundToggle={
              supportsSound
                ? () => setSoundEnabled(!soundEnabled)
                : undefined
            }
            modelWarning={modelWarning}
            getModelCost={(id) => getVideoModelCost(id, availableModels, videoDuration, videoResolution)}
            onDisabledClick={handleDisabledClick}
          />
        ) : undefined
      }
      lightbox={
        selectedMedia ? (
          <VideoLightbox
            videoUrl={selectedMedia.url}
            sourceImageUrl={selectedMedia.referenceImageUrls[0]}
            prompt={selectedMedia.prompt}
            createdAt={selectedMedia.createdAt}
            model={selectedMedia.model}
            resolution={selectedMedia.resolution}
            aspectRatio={selectedMedia.aspectRatio}
            duration={selectedMedia.duration}
            pipeline={selectedMedia.pipeline}
            onClose={handleCloseLightbox}
            onRegenerate={handleRegenerate}
            onDownload={handleDownload}
            onShare={handleShare}
            navigation={
              galleryVideos.length > 1
                ? {
                    currentIndex: currentMediaIndex,
                    totalItems: galleryVideos.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
    >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
          <div data-scroll-target="prompt">
            <VideoPromptArea
              modelDisplayName={modelDisplayName}
              promptPlaceholder={promptPlaceholder}
              showFeaturedSuggestion={supportsEndFrame}
              prompt={videoPrompt}
              onPromptChange={setVideoPrompt}
              onEnhancePrompt={handleEnhancePrompt}
              isEnhancing={isEnhancingVideoPrompt}
            />
          </div>

          <div data-scroll-target="upload">
            <VideoSourceUpload
            supportsEndFrame={supportsEndFrame}
            fileInputRef={fileInputRef}
            filePreview={filePreview}
            endFramePreview={endFramePreview}
            onInputChange={handleInputChange}
            onClearFile={clearFile}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onEndFrameUpload={handleEndFrameUpload}
            onEndFrameClear={() => setEndFramePreview(null)}
            onSwapFrames={swapFrames}
          />
          </div>
        </div>

        {/* Video Gallery */}
        <CreationsGallery
          title={t.generate.yourVideos}
          galleryRef={galleryRef}
          pendingCount={pendingCount}
          pendingLabel={t.generate.creatingVideo}
          pendingItems={dbPendingVideos.map((v) => ({ id: v.id, createdAt: v.created_at }))}
          pendingMediaType="video"
          completingIds={videoCompletingIds}
          className="mt-10 mb-10"
          emptyIcon={Video}
          emptyTitle={t.generate.noVideosYet}
          emptySubtitle={t.generate.startCreatingVideos}
        >
            {galleryVideos.map((video) => (
              <MediaGridItem
                key={video.id}
                imageUrl={video.displayUrl || undefined}
                videoUrl={video.url || undefined}
                isVideo
                alt={video.prompt}
                onClick={() => setSelectedMedia(video)}
                onDownload={
                  video.url ? () => downloadMedia(video.url!, "todoai-video", "mp4") : undefined
                }
                downloadLabel={t.lightbox.download}
              >
                <GalleryItemBadges item={video} />
              </MediaGridItem>
            ))}
        </CreationsGallery>
    </StudioPageLayout>
  );
};

export default FramesToVideoPage;
